import React, { forwardRef, memo, useContext, SVGAttributes } from 'react';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { definedAccessorXY, getScaleBandWidth, noTransition } from './utils';
import { SeriesPointXY0, SeriesPointX0Y } from './Types';
import VizContext from './VizContext';

export interface Props extends SVGAttributes<SVGGElement> {
  /**
   * Flag for enabling animations.
   */
  animate?: boolean;
  /**
   * Flag to clip the path to the artboard area.
   */
  clipToArtboard?: boolean;
  /**
   * When the Y axis has band scaling, this provides the ability
   * to set a maximum height on the bars that sit within that
   * band.
   */
  maxHeight?: number;
  /**
   * When the X axis has band scaling, this provides the ability
   * to set a maximum width on the bars that sit within that
   * band.
   */
  maxWidth?: number;
  /**
   * Minimum height to apply to the drawn rectangle.
   */
  minHeight?: number;
  /**
   * Minimum width to apply to the drawn rectangle
   */
  minWidth?: number;
  /**
   * Offsets the height in pixels, useful for creating a slight
   * overlap or gap with stacked columns.
   */
  offsetHeight?: number;
  /**
   * Offsets the width in pixels, useful for creating a slight
   * overlap or gap with stacked bars.
   */
  offsetWidth?: number;
  /**
   * Series of data point objects, with X and Y values that lie within
   * the given domains.
   */
  series: (SeriesPointX0Y | SeriesPointXY0)[];
}

/**
 * Rect series component, that draws a rect for each data points given within
 * the series.
 */
export const VizSVGRects = forwardRef<SVGGElement, Props>((props: Props, ref) => {
  const {
    animate,
    className,
    clipToArtboard,
    opacity = 1,
    maxHeight,
    maxWidth,
    minHeight = 0,
    minWidth = 0,
    offsetHeight = 0,
    offsetWidth = 0,
    series,
    ...rest
  } = props;

  const {
    defArtboardClipMaskId,
    artboardWidth,
    artboardHeight,
    getX,
    getX0,
    getY,
    getY0,
    transition,
    xDomain,
    xScale,
    yDomain,
    yScale,
  } = useContext(VizContext);

  const xBandWidth = getScaleBandWidth(xScale);
  const yBandWidth = getScaleBandWidth(yScale);
  const isVert = yBandWidth === 0;

  const xSize = isVert && maxWidth !== undefined ? Math.min(maxWidth, xBandWidth) : xBandWidth;
  const ySize = isVert || maxHeight === undefined ? yBandWidth : Math.min(maxHeight, yBandWidth);

  const posXDomain0 = getX(xDomain[0]) as number;
  const posXDomain1 = getX(xDomain[xDomain.length - 1]) as number;
  const posYDomain0 = getY(yDomain[0]) as number;
  const posYDomain1 = getY(yDomain[yDomain.length - 1]) as number;

  const minHeightScale = minHeight / artboardHeight;
  const minWidthScale = minWidth / artboardWidth;

  const offsetHeightScale = offsetHeight / artboardHeight;
  const offsetWidthScale = offsetWidth / artboardWidth;

  return (
    <g { ...rest }
        className={ classnames('Bitviz__series-rects', className) }
        clipPath={ clipToArtboard ? `url(#${defArtboardClipMaskId})` : undefined }
        ref={ ref }>
      { series
        .filter(definedAccessorXY)
        .map((point, index) => {
          const posX = getX(point.x) as number;
          const posX0 = getX0((point as SeriesPointX0Y).x0) as number;
          const posY = getY(point.y) as number;
          const posY0 = getY0((point as SeriesPointXY0).y0) as number;

          return (
            <motion.rect
                animate={ {
                  opacity: opacity,
                  scaleX: isVert ? 1 : Math.max(minWidthScale, ((posX - posX0) / (posXDomain1 - posXDomain0)) + offsetWidthScale),
                  scaleY: isVert ? Math.min(minHeightScale * -1, ((posY - posY0) / (posYDomain1 - posYDomain0) + offsetHeightScale) * -1) : 1,
                  height: isVert ? artboardHeight : ySize,
                  width: isVert ? xSize : artboardWidth,
                  x: isVert ? posX - xSize / 2 : posX0,
                  y: isVert ? posY0 : posY - ySize / 2,
                } }
                height={ artboardHeight }
                initial={ {
                  opacity: 0,
                  originX: isVert ? 0 : xDomain[0] > xDomain[1] ? 1 : 0,
                  originY: isVert ? yDomain[0] > yDomain[1] ? 1 : 0 : 0,
                  scaleX: isVert ? 1 : 0,
                  scaleY: isVert ? 0 : 1,
                  height: isVert ? artboardHeight : ySize,
                  width: isVert ? xSize : artboardWidth,
                  x: isVert ? posX - xSize / 2 : posX0,
                  y: isVert ? posY0 : posY - ySize / 2,
                } }
                key={ index }
                transition={ animate ? transition : noTransition } />
          );
        }) }
    </g>
  );
});

export default memo(VizSVGRects);
